import {
  CreateReducer,
  CreateAction,
} from '../../store/utils/store'

import {
  getProjectHandlers,
} from '../../store/utils/api'

import {
  IRecord_Marketing_Campaign,
  IRecord_Marketing_Campaign_Data,
  IRecord_Marketing_Post,
  IQuery_Marketing_List_Posts,
  IRecord_Marketing_Post_Core,
  IRecord_Marketing_Post_Result,
  IQuery_Org_Campaign,
  IQuery_Org_Campaign_Post,
  IQuery_Org_Campaign_Template,
  IRecord_Marketing_Connection,
  IData_Marketing_TextEditor_Prompt,
  IData_Marketing_Project_Settings,
} from '@kaidam/fc-shared/src/types/schema/marketing'

import {
  MarketingState,
  ReducerHandler,
  ThunkHandler,
} from '../../types/store'

import {
  NetworkThunk,
} from '../utils/network'

import {
  PAGINATION_RESULTS_DEFAULT,
  PAGINATION_MARKETING_CAMPAIGNS_SORT_DESCRIPTOR,
  PAGINATION_MARKETING_POSTS_SORT_DESCRIPTOR
} from '@kaidam/fc-shared/src/constants'

import {
  IPubsub_Message_Type_Marketing_Post_Event,
  IPubsub_Message_Body_Marketing_Post_Event,
} from '@kaidam/fc-shared/src/types/pubsub'

import {
  IData_Paginated_Results_Wrapper,
} from '@kaidam/fc-shared/src/types/pagination'

import {
  getSocketClientFactory,
} from '../../store/utils/websocket'

import {
  getPaginatedQuery,
  getPaginationQueryParams,
} from '@kaidam/fc-shared/src/utils/pagination'

import {
  getDateString,
} from '@kaidam/fc-shared/src/utils/marketing'

import {
  marketingPostChannel,
} from '@kaidam/fc-shared/src/pubsub/channels'

import projectActions from '../modules/project'
import projectSelectors from '../selectors/project'
import marketingSelectors from '../selectors/marketing'
import routerSelectors from '../selectors/router'
import snackbarActions from '../modules/snackbar'

const getPostUpdateSocket = getSocketClientFactory<
  IPubsub_Message_Type_Marketing_Post_Event,
  IPubsub_Message_Body_Marketing_Post_Event
>()


const prefix = 'marketing'

/*

  initial state

*/
const initialState: MarketingState = {
  campaigns: [],
  campaignsPagination: PAGINATION_RESULTS_DEFAULT,
  posts: [],
  local_posts: [],
  godContent: {},
  allPosts: [],
  allPostsPagination: PAGINATION_RESULTS_DEFAULT,
  connections: [],
}

const setCampaigns: ReducerHandler<MarketingState, IData_Paginated_Results_Wrapper<IRecord_Marketing_Campaign>> = (state, action) => {
  const {
    data,
    pagination,
  } = action.payload
  state.campaigns = data
  state.campaignsPagination = pagination
}

const setAllPosts: ReducerHandler<MarketingState, IData_Paginated_Results_Wrapper<IRecord_Marketing_Post>> = (state, action) => {
  const {
    data,
    pagination,
  } = action.payload
  state.allPosts = data
  state.allPostsPagination = pagination
}

const setCampaignsRaw: ReducerHandler<MarketingState, IRecord_Marketing_Campaign[]> = (state, action) => {
  state.campaigns = action.payload
}

const setPosts: ReducerHandler<MarketingState, IRecord_Marketing_Post[]> = (state, action) => {
  state.posts = action.payload
}

const setLocalPosts: ReducerHandler<MarketingState, IRecord_Marketing_Post[]> = (state, action) => {
  state.local_posts = action.payload
}

const setGodContentValues: ReducerHandler<MarketingState, Record<string, string>> = (state, action) => {
  state.godContent = action.payload
}

const setConnections: ReducerHandler<MarketingState, IRecord_Marketing_Connection[]> = (state, action) => {
  state.connections = action.payload
}

/*

  templates

*/

const loadGodContent: ThunkHandler<string, void> = (orgId: string) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<Record<string, string>>(`/marketing/${orgId}/settings`)
  dispatch(actions.setGodContentValues(data))
}

const loadCampaigns: ThunkHandler<void, void> = () => async (dispatch, getState) => {
  const params = routerSelectors.params(getState())
  
  const paginatedQuery = getPaginatedQuery(
    params,
    {},
    PAGINATION_MARKETING_CAMPAIGNS_SORT_DESCRIPTOR,
  )

  const paginationQueryParams = getPaginationQueryParams(paginatedQuery.pagination)
  const allQueryParams = Object.assign({}, paginationQueryParams, {})

  const orgid = projectSelectors.currentOrgId(getState())
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<IData_Paginated_Results_Wrapper<IRecord_Marketing_Campaign>>(`/marketing/${orgid}/campaigns`, {
    params: allQueryParams
  })
  dispatch(actions.setCampaigns(data))
}

const loadAllPosts: ThunkHandler<void, void> = () => async (dispatch, getState) => {
  const params = routerSelectors.params(getState())

  const paginatedQuery = getPaginatedQuery(
    params,
    {
      campaignid: params['campaignid'],
      network: params['network'],
      after: params['after'],
      before: params['before'],
      orgid: params['orgid'],
      scope: params['scope'],
      state: params['state'],
      type: params['type'],
    },
    PAGINATION_MARKETING_POSTS_SORT_DESCRIPTOR,
  )

  const paginationQueryParams = getPaginationQueryParams(paginatedQuery.pagination)
  const allQueryParams = Object.assign({}, paginationQueryParams, paginatedQuery.query)

  let orgid = projectSelectors.currentOrgId(getState())
  if(params['orgid']) {
    orgid = params['orgid']
  }
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<IData_Paginated_Results_Wrapper<IRecord_Marketing_Post>>(`/marketing/${orgid}/paginated_posts`, {
    params: allQueryParams
  })
  dispatch(actions.setAllPosts(data))
}

const loadCampaignsForSelect: ThunkHandler<void, void> = () => async (dispatch, getState) => {
  const paginatedQuery = getPaginatedQuery(
    {
      pagination_page_size: '1000',
    },
    {},
    PAGINATION_MARKETING_CAMPAIGNS_SORT_DESCRIPTOR,
  )

  const paginationQueryParams = getPaginationQueryParams(paginatedQuery.pagination)
  const allQueryParams = Object.assign({}, paginationQueryParams, {})
  const orgid = marketingSelectors.loadCampaignsOrgId(getState())
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<IData_Paginated_Results_Wrapper<IRecord_Marketing_Campaign>>(`/marketing/${orgid}/campaigns`, {
    params: allQueryParams
  })
  dispatch(actions.setCampaigns(data))
}

const loadPosts: ThunkHandler<IQuery_Marketing_List_Posts, void> = (params) => async (dispatch, getState) => {
  const orgid = projectSelectors.currentOrgId(getState())
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<IRecord_Marketing_Post[]>(`/marketing/${orgid}/posts`, {
    params,
  })
  dispatch(actions.setPosts(data))
}

const loadConnections: ThunkHandler<void, void> = () => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<IRecord_Marketing_Connection[]>(`/marketing/connections`)
  dispatch(actions.setConnections(data))
}

const addCampaign: ThunkHandler<{
  data: IRecord_Marketing_Campaign_Data,
}, {
  org: string,
  id: string,
}> = (payload) => async (dispatch, getState) => {
  const orgId = projectSelectors.currentOrgId(getState())
  const handlers = getProjectHandlers(getState)
  const campaign = await handlers.post<IRecord_Marketing_Campaign_Data, IRecord_Marketing_Campaign>(`/marketing/${orgId}/campaigns`, payload.data)
  await dispatch(actions.loadCampaigns())
  dispatch(snackbarActions.setSuccess(`campaign added`))
  return {
    org: campaign.org,
    id: campaign.id,
  }
}

const updateCampaign: ThunkHandler<{
  id: string,
  orgid: string,
  data: Partial<IRecord_Marketing_Campaign_Data>,
}, boolean> = (payload) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  await handlers.put<Partial<IRecord_Marketing_Campaign_Data>, void>(`/marketing/${payload.orgid}/campaigns/${payload.id}`, payload.data)
  await dispatch(actions.loadCampaigns())
  dispatch(snackbarActions.setSuccess(`campaign updated`))
  return true
}

const updatePost: ThunkHandler<{
  id: string,
  orgid: string,
  data: Partial<IRecord_Marketing_Post_Core>,
  updateAllSeriesTimes?: boolean,
}, boolean> = (payload) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  await handlers.put<Partial<IRecord_Marketing_Post_Core>, void>(`/marketing/${payload.orgid}/posts/${payload.id}`, payload.data, {
    params: {
      updateAllSeriesTimes: payload.updateAllSeriesTimes ? 'yes' : '',
    }
  })
  dispatch(snackbarActions.setSuccess(payload.updateAllSeriesTimes ? `all posts updated` : `post updated`))
  return true
}

const deleteCampaign: ThunkHandler<{
  id: string,
  orgid: string,
}, boolean> = (payload) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  await handlers.delete(`/marketing/${payload.orgid}/campaigns/${payload.id}`)
  await dispatch(actions.loadCampaigns())
  dispatch(snackbarActions.setSuccess(`campaign deleted`))
  return true
}

const updateGodContent: ThunkHandler<{
  orgid: string,
  data: Record<string, string>,
}, boolean> = (payload) => async (dispatch, getState) => {
  const projectHandlers = getProjectHandlers(getState)
  await projectHandlers.put(`/marketing/${payload.orgid}/settings`, payload.data)
  await dispatch(projectActions.list())
  return true
}

const updatePrompts: ThunkHandler<IData_Marketing_Project_Settings, boolean> = (data) => async (dispatch, getState) => {
  const projectHandlers = getProjectHandlers(getState)
  await projectHandlers.put(`/marketing/prompts`, data)
  await dispatch(projectActions.list())
  return true
}

const loadNewThemes: ThunkHandler<IQuery_Org_Campaign, string[]> = ({
  orgid,
  campaignid,
}) => async (dispatch, getState) => {
  const projectHandlers = getProjectHandlers(getState)
  const data: string[] = await projectHandlers.post(`/marketing/${orgid}/campaigns/${campaignid}/new_themes`)
  return data
}

const reloadPostContent: ThunkHandler<IQuery_Org_Campaign_Post, IRecord_Marketing_Post_Result> = ({
  orgid,
  campaignid,
  postid,
}) => async (dispatch, getState) => {
  const projectHandlers = getProjectHandlers(getState)
  const result = await projectHandlers.post<void, IRecord_Marketing_Post_Result>(`/marketing/${orgid}/campaigns/${campaignid}/posts/${postid}/reload`)
  return result
}

const sendPost: ThunkHandler<IQuery_Org_Campaign_Post, boolean> = ({
  orgid,
  campaignid,
  postid,
}) => async (dispatch, getState) => {
  const projectHandlers = getProjectHandlers(getState)
  await projectHandlers.post<void, void>(`/marketing/${orgid}/campaigns/${campaignid}/posts/${postid}/send`)
  return true
}

const retryPostContent: ThunkHandler<IQuery_Org_Campaign_Post, IRecord_Marketing_Post_Result> = ({
  orgid,
  campaignid,
  postid,
}) => async (dispatch, getState) => {
  const projectHandlers = getProjectHandlers(getState)
  const result = await projectHandlers.put<void, IRecord_Marketing_Post_Result>(`/marketing/${orgid}/campaigns/${campaignid}/posts/${postid}/retry`)
  return result
}

const loadLocalPosts: ThunkHandler<IQuery_Org_Campaign_Post, void> = ({
  orgid,
  campaignid,
  postid,
}) => async (dispatch, getState) => {
  const projectHandlers = getProjectHandlers(getState)
  const result = await projectHandlers.get<IRecord_Marketing_Post[]>(`/marketing/${orgid}/campaigns/${campaignid}/posts/${postid}/local`)
  dispatch(actions.setLocalPosts(result))
}

const loadPostContent: ThunkHandler<IQuery_Org_Campaign_Template, boolean> = ({
  orgid,
  campaignid,
  templateid,
}) => async (dispatch, getState) => {
  // clear out the example content in the store because thats how the loading spinner works
  const newCampaigns = marketingSelectors
    .campaigns(getState())
    .map(campaign => {
      if(campaign.id !== campaignid) {
        return campaign
      }
      const newExamplePosts = Object.assign({}, campaign.data.example_posts)
      delete(newExamplePosts[templateid])
      const newData = Object.assign({}, campaign.data, {
        example_posts: newExamplePosts,
      })
      return Object.assign({}, campaign, {
        data: newData,
      })
    })
  dispatch(actions.setCampaignsRaw(newCampaigns))
  const campaign = newCampaigns.find(c => c.id == campaignid)
  const projectHandlers = getProjectHandlers(getState)
  await projectHandlers.post(`/marketing/${orgid}/campaigns/${campaignid}/posts/${templateid}/content`, {
    theme: campaign.data.example_theme, 
  })
  await dispatch(actions.loadCampaigns())
  return true
}

const localisePostContent: ThunkHandler<IQuery_Org_Campaign_Post & {
  localiseid: string,
  original_post: string,
}, boolean> = ({
  orgid,
  campaignid,
  postid,
  localiseid,
  original_post,
}) => async (dispatch, getState) => {
  // clear out the example content in the store because thats how the loading spinner works
  const newCampaigns = marketingSelectors
    .campaigns(getState())
    .map(campaign => {
      if(campaign.id !== campaignid) {
        return campaign
      }
      const newExamplePosts = Object.assign({}, campaign.data.example_posts)
      delete(newExamplePosts[`${postid}_local`])
      const newData = Object.assign({}, campaign.data, {
        example_posts: newExamplePosts,
      })
      return Object.assign({}, campaign, {
        data: newData,
      })
    })
  dispatch(actions.setCampaignsRaw(newCampaigns))
  const projectHandlers = getProjectHandlers(getState)
  await projectHandlers.post(`/marketing/${orgid}/campaigns/${campaignid}/posts/${postid}/localise/${localiseid}`, {
    original_post,
  })
  await dispatch(actions.loadCampaigns())
  return true
}

/*

  websockets

*/
const subscribeToPostUpdates: ThunkHandler<IQuery_Org_Campaign_Post & {
  callback?: {
    (): Promise<void>,
  }
}, boolean> = (query) => async (dispatch, getState) => {
  const {project} = projectSelectors.projectInfo(getState())
  if(!project) return false
  const channel = marketingPostChannel.get(project, query.postid)
  const socket = getPostUpdateSocket()
  await socket.subscribe(channel, async (message) => {
    if(query.callback) query.callback()
  })
  return true
}

const unsubscribeToPostUpdates: ThunkHandler<IQuery_Org_Campaign_Post, boolean> = (query) => async (dispatch, getState) => {
  const {project} = projectSelectors.projectInfo(getState())
  if(!project) return false
  const channel = marketingPostChannel.get(project, query.postid)
  const socket = getPostUpdateSocket()
  socket.unsubscribe(channel)
  return true
}

const getConnectionURL: ThunkHandler<{
  orgid: string,
}, string> = ({
  orgid,
}) => async (dispatch, getState) => {
  const projectHandlers = getProjectHandlers(getState)
  const result = await projectHandlers.post<void, {url: string}>(`/marketing/${orgid}/connection_url`) 
  return result.url
}

const getSuggestion: ThunkHandler<{
  orgid: string,
  prompt: IData_Marketing_TextEditor_Prompt,
  text: string,
}, {
  text: string,
}> = ({
  orgid,
  prompt,
  text,
}) => async (dispatch, getState) => {
  const projectHandlers = getProjectHandlers(getState)
  const result = await projectHandlers.post<{
    prompt: IData_Marketing_TextEditor_Prompt,
    text: string,
  }, {
    text: string,
  }>(`/marketing/${orgid}/suggestion`, {
    prompt,
    text,
  })
  return result
}

/*

  collections

*/
const reducers = {
  setCampaigns,
  setAllPosts,
  setCampaignsRaw,
  setGodContentValues,
  setPosts,
  setLocalPosts,
  setConnections,
}

export const actions = {
  setCampaigns: CreateAction(prefix, `setCampaigns`, reducers.setCampaigns),
  setAllPosts: CreateAction(prefix, `setAllPosts`, reducers.setAllPosts),
  setCampaignsRaw: CreateAction(prefix, `setCampaignsRaw`, reducers.setCampaignsRaw),
  setGodContentValues: CreateAction(prefix, `setGodContentValues`, reducers.setGodContentValues),
  setPosts: CreateAction(prefix, `setPosts`, reducers.setPosts),
  setLocalPosts: CreateAction(prefix, `setLocalPosts`, reducers.setLocalPosts),
  setConnections: CreateAction(prefix, `setConnections`, reducers.setConnections),
  loadGodContent: NetworkThunk(prefix, 'loadGodContent', loadGodContent, {
    snackbar: true,
  }),
  loadCampaigns: NetworkThunk(prefix, 'loadCampaigns', loadCampaigns, {
    snackbar: true,
  }),
  loadAllPosts: NetworkThunk(prefix, 'loadAllPosts', loadAllPosts, {
    snackbar: true,
  }),
  loadCampaignsForSelect: NetworkThunk(prefix, 'loadCampaignsForSelect', loadCampaignsForSelect, {
    snackbar: true,
  }),
  loadPosts: NetworkThunk(prefix, 'loadPosts', loadPosts, {
    snackbar: true,
  }),
  loadLocalPosts: NetworkThunk(prefix, 'loadLocalPosts', loadLocalPosts, {
    snackbar: true,
  }),
  loadConnections: NetworkThunk(prefix, 'loadConnections', loadConnections, {
    snackbar: true,
  }),
  addCampaign: NetworkThunk(prefix, 'addCampaign', addCampaign, {
    snackbar: true,
    globalLoading: {
      message: 'adding campaign...'
    },
  }),
  updateCampaign: NetworkThunk(prefix, 'updateCampaign', updateCampaign, {
    snackbar: true,
    globalLoading: {
      message: 'updating campaign...'
    },
  }),
  updatePost: NetworkThunk(prefix, 'updatePost', updatePost, {
    snackbar: true,
    globalLoading: {
      message: 'updating post...'
    },
  }),
  deleteCampaign: NetworkThunk(prefix, 'deleteCampaign', deleteCampaign, {
    snackbar: true,
    globalLoading: {
      message: 'deleting campaign...'
    },
  }),
  updateGodContent: NetworkThunk(prefix, 'updateGodContent', updateGodContent, {
    snackbar: true,
    globalLoading: {
      message: 'updating settings...'
    },
  }),
  updatePrompts: NetworkThunk(prefix, 'updatePrompts', updatePrompts, {
    snackbar: true,
    globalLoading: {
      message: 'updating prompts...'
    },
  }),
  loadNewThemes: NetworkThunk(prefix, 'loadNewThemes', loadNewThemes, {
    snackbar: true,
    globalLoading: {
      message: 'loading new themes...'
    },
  }),
  reloadPostContent: NetworkThunk(prefix, 'reloadPostContent', reloadPostContent, {
    snackbar: true,
  }),
  sendPost: NetworkThunk(prefix, 'sendPost', sendPost, {
    snackbar: true,
    globalLoading: {
      message: 'sending post to network...'
    },
  }),
  retryPostContent: NetworkThunk(prefix, 'retryPostContent', retryPostContent, {
    snackbar: true,
    globalLoading: {
      message: 'retrying post content...'
    },
  }),
  loadPostContent: NetworkThunk(prefix, 'loadPostContent', loadPostContent, {
    snackbar: true,
  }),
  localisePostContent: NetworkThunk(prefix, 'localisePostContent', localisePostContent, {
    snackbar: true,
  }),
  subscribeToPostUpdates: NetworkThunk(prefix, 'subscribeToPostUpdates', subscribeToPostUpdates, {
    snackbar: true,
  }),
  unsubscribeToPostUpdates: NetworkThunk(prefix, 'unsubscribeToPostUpdates', unsubscribeToPostUpdates, {
    snackbar: true,
  }),
  getConnectionURL: NetworkThunk(prefix, 'getConnectionURL', getConnectionURL, {
    snackbar: true,
  }),
  getSuggestion: NetworkThunk(prefix, 'getSuggestion', getSuggestion, {
    snackbar: true,
    globalLoading: {
      message: 'getting suggestion...'
    },
  }),
}

export const reducer = CreateReducer<MarketingState>({
  prefix,
  initialState,
  reducers,
})

export default actions